import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import Logo from '@/components/icons/Logo'
import { ThemeToggle } from '@/components/theme/ThemeToggle'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login({ email, password })
      navigate('/')
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Login failed. Please check your credentials.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-primary px-4">
      <div className="fixed right-4 top-4 z-50">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md rounded-modal border border-border-default bg-surface-secondary px-8 pt-8 pb-8">
        <div className="mb-6 flex justify-center text-text-primary">
          <Logo height={60} color="currentColor" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-label-default text-text-secondary">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-body-default h-[38px] w-full rounded-md border border-border-default bg-surface-primary px-3 py-2 text-text-primary placeholder:text-text-disabled focus:border-fill-primary focus:outline-none focus:ring-1 focus:ring-fill-primary"
              placeholder="user@example.com"
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-label-default text-text-secondary">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="text-body-default h-[38px] w-full rounded-md border border-border-default bg-surface-primary px-3 py-2 text-text-primary placeholder:text-text-disabled focus:border-fill-primary focus:outline-none focus:ring-1 focus:ring-fill-primary"
              placeholder="••••••••"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-md border border-fill-destructive/30 bg-fill-destructive/10 p-3">
              <p className="text-body-small text-fill-destructive">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="text-label-default flex h-9 w-full items-center justify-center rounded-md bg-fill-primary text-text-inverse hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-fill-primary focus:ring-offset-2 focus:ring-offset-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
